/* Shared crossfade galleries. Native vertical scrolling and thumbnail scrolling; no dependencies. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function dragScroll(rail, signal, release) {
    let pointer, blockedUntil = 0;
    const finish = event => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const ended = pointer;
      pointer = null;
      rail.classList.remove('is-dragging');
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      if (ended.moved) blockedUntil = performance.now() + 450;
      if (ended.dragging) release?.(event.type === 'pointercancel' ? 0 : (performance.now() - ended.time > 100 ? 0 : ended.velocity));
    };
    rail.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.button !== 0) return;
      blockedUntil = 0;
      pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft,
        lastX: event.clientX, time: performance.now(), velocity: 0, mouse: event.pointerType === 'mouse', moved: false};
    }, {signal});
    rail.addEventListener('pointermove', event => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      if (Math.hypot(dx, dy) > 10) pointer.moved = true;
      if (!pointer.mouse) return; // The browser owns touch inertia, vertical scrolling and pinch zoom.
      if (!pointer.dragging) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { pointer = null; return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
        pointer.dragging = true;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      const now = performance.now();
      pointer.velocity = (pointer.lastX - event.clientX) / Math.max(1, now - pointer.time);
      pointer.lastX = event.clientX; pointer.time = now;
      rail.scrollLeft = pointer.left - dx;
    }, {signal});
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => rail.addEventListener(type, finish, {signal}));
    rail.addEventListener('pointerleave', event => { if (!pointer?.dragging) finish(event); }, {signal});
    rail.addEventListener('dragstart', event => event.preventDefault(), {signal});
    rail.addEventListener('click', event => {
      if (performance.now() < blockedUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, {capture: true, signal});
    signal.addEventListener('abort', () => { pointer = null; rail.classList.remove('is-dragging'); }, {once: true});
  }

  class SwipeRail {
    constructor(rail, {items, initialIndex = 0, duration, onChange = () => {}} = {}) {
      this.rail = rail; this.onChange = onChange; this.index = -1; this.job = 0;
      this.duration = duration || (rail.matches('.hero-images') ? 650 : 420);
      this.events = new AbortController();
      const signal = this.events.signal;
      rail.classList.add('yv-swipe-rail', 'yv-fade-rail');
      rail.tabIndex = 0; rail.yvSlider = this;
      let surface = rail, background = 'rgba(0, 0, 0, 0)';
      while (surface && background === 'rgba(0, 0, 0, 0)') {
        background = getComputedStyle(surface).backgroundColor; surface = surface.parentElement;
      }
      rail.style.setProperty('--yv-photo-background', background === 'rgba(0, 0, 0, 0)' ? '#f5ebdd' : background);
      this.blockedUntil = 0;
      rail.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || this.items.length < 2 || event.target.closest('video, iframe, [data-yv-player-controls]')) return;
        this.stop(); this.blockedUntil = 0;
        this.pointer = {id: event.pointerId, x: event.clientX, y: event.clientY,
          last: event.clientX, time: performance.now(), velocity: 0, delta: 0,
          index: this.index, weights: this.weights.slice(), dragging: false};
      }, {signal});
      rail.addEventListener('pointermove', event => {
        const p = this.pointer;
        if (!p || p.id !== event.pointerId) return;
        const dx = event.clientX - p.x, dy = event.clientY - p.y;
        if (!p.dragging) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.pointer = null; this.go(this.index); return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
          p.dragging = true; rail.classList.add('is-dragging'); rail.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        const now = performance.now(), width = Math.max(1, rail.clientWidth);
        p.velocity = .55 * p.velocity + .45 * (p.last - event.clientX) / width / Math.max(1, now - p.time) * 1000;
        p.last = event.clientX; p.time = now; p.delta = -dx / width;
        const next = clamp(p.index + Math.sign(p.delta), 0, this.items.length - 1);
        this.preload(next);
        if (!reduced.matches && this.loaded(next)) {
          const amount = clamp(Math.abs(p.delta), 0, 1);
          this.weights = p.weights.map((weight, i) => weight * (1 - amount) + (i === next ? amount : 0));
          this.draw();
        }
      }, {signal});
      const finish = event => {
        const p = this.pointer;
        if (!p || p.id !== event.pointerId) return;
        this.pointer = null; rail.classList.remove('is-dragging');
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        if (!p.dragging) { this.go(this.index); return; }
        this.blockedUntil = performance.now() + 450;
        const velocity = performance.now() - p.time > 100 ? 0 : p.velocity;
        const direction = Math.abs(velocity) > .6 ? Math.sign(velocity) : Math.abs(p.delta) > .18 ? Math.sign(p.delta) : 0;
        this.go(event.type === 'pointercancel' ? p.index : p.index + direction);
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => rail.addEventListener(type, finish, {signal}));
      rail.addEventListener('pointerleave', event => { if (!this.pointer?.dragging) finish(event); }, {signal});
      rail.addEventListener('dragstart', event => event.preventDefault(), {signal});
      rail.addEventListener('click', event => {
        if (performance.now() < this.blockedUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, {capture: true, signal});
      rail.addEventListener('keydown', event => {
        if (event.target.closest('video, iframe, input, select, textarea, [data-yv-player-controls]')) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        if (event.key === 'Home') this.go(0);
        else if (event.key === 'End') this.go(this.items.length - 1);
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, {signal});
      let wheel = 0, lastWheel = 0, wheelUsed = false;
      rail.addEventListener('wheel', event => {
        if (event.target.closest('video, iframe')) return;
        if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        const now = performance.now();
        if (now - lastWheel > 180) { wheel = 0; wheelUsed = false; }
        lastWheel = now; wheel += event.deltaX;
        if (!wheelUsed && Math.abs(wheel) > 45) { wheelUsed = true; this.move(Math.sign(wheel)); }
      }, {passive: false, signal});
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.preload(); this.observer.disconnect(); }
      }, {rootMargin: '250px'});
      this.observer.observe(rail);
      this.reset(items || [...rail.children], initialIndex);
      reduced.addEventListener('change', () => {
        if (reduced.matches) { this.pointer = null; rail.classList.remove('is-dragging'); this.go(this.target ?? this.index, false); }
      }, {signal});
    }
    images(index) {
      const item = this.items[index];
      return !item ? [] : item.matches('img') ? [item] : [...item.querySelectorAll('img')];
    }
    loaded(index) { return this.images(index).every(image => image.complete && image.naturalWidth > 0); }
    stop() {
      this.job++; cancelAnimationFrame(this.animation); this.animation = null;
      this.rail.removeAttribute('aria-busy');
    }
    reset(items, index = 0) {
      this.stop(); this.pointer = null; this.rail.classList.remove('is-dragging');
      this.items = items; this.index = -1; this.target = null;
      this.items.forEach(item => {
        item.classList.add('yv-swipe-slide'); item.hidden = false;
        this.images(this.items.indexOf(item)).forEach(image => { image.draggable = false; });
      });
      this.weights = items.map((_, i) => i === index ? 1 : 0);
      this.go(index, false);
    }
    async go(index, smooth = true) {
      if (!this.items.length) return;
      index = clamp(index, 0, this.items.length - 1);
      this.stop(); const job = this.job; this.target = index;
      this.preload(index);
      if (smooth && !this.loaded(index)) {
        this.rail.setAttribute('aria-busy', 'true');
        this.images(index).forEach(image => { image.loading = 'eager'; });
        await Promise.all(this.images(index).map(image => image.decode().catch(() => {})));
        if (job !== this.job) return;
        this.rail.removeAttribute('aria-busy');
        if (!this.loaded(index)) { this.go(this.index, false); return; }
      }
      const from = this.weights.slice();
      this.sync(index);
      const distance = 1 - (from[index] || 0);
      if (!smooth || reduced.matches || !this.rail.clientWidth || distance < .0001) {
        this.weights = this.items.map((_, i) => i === index ? 1 : 0); this.draw(); return;
      }
      const start = performance.now(), duration = Math.max(180, this.duration * distance);
      const tick = now => {
        if (job !== this.job) return;
        const t = clamp((now - start) / duration, 0, 1), ease = t * t * (3 - 2 * t);
        this.weights = from.map((weight, i) => weight * (1 - ease) + (i === index ? ease : 0));
        this.draw(); this.animation = t < 1 ? requestAnimationFrame(tick) : null;
      };
      this.animation = requestAnimationFrame(tick);
    }
    move(direction) { this.go((this.target ?? this.index) + direction); }
    sync(index) {
      if (!this.items[index]) return;
      if (this.items.some((item, i) => i !== index && item.contains(document.activeElement))) this.rail.focus({preventScroll: true});
      this.items.forEach((item, i) => {
        item.dataset.active = String(i === index); item.setAttribute('aria-hidden', String(i !== index)); item.inert = i !== index;
      });
      if (index === this.index) return;
      this.index = index; this.preload(); this.onChange(index, this.items[index], this.items.length);
    }
    draw() {
      // Convert image weights to stacked alpha: the bottom stays opaque, so there is no background flash.
      // Direct thumbnail jumps blend only the chosen views, including when a fade is interrupted.
      let total = 0;
      this.items.forEach((item, i) => {
        total += this.weights[i];
        item.style.opacity = String(i === 0 ? 1 : total > 0 ? this.weights[i] / total : 0);
        item.style.zIndex = String(i);
      });
      this.value = this.weights.reduce((sum, weight, i) => sum + weight * i, 0);
      this.rail.dataset.position = this.value.toFixed(4);
      this.rail.dispatchEvent(new CustomEvent('yv:fade-progress', {detail: {weights: this.weights}}));
    }
    preload(index = this.index) {
      const bounds = this.rail.getBoundingClientRect();
      if (!bounds.width || bounds.top > innerHeight + 300 || bounds.bottom < -300) return;
      for (let i = Math.max(0, index - 1); i < Math.min(this.items.length, index + 2); i++) this.images(i).forEach(image => { image.loading = 'eager'; });
    }
    destroy() {
      this.stop(); this.events.abort(); this.observer.disconnect(); this.pointer = null;
      if (this.rail.yvSlider === this) delete this.rail.yvSlider;
    }
  }
  function photoDots(container, count, index, choose) {
    if (!container) return;
    if (container.childElementCount !== count) {
      container.replaceChildren(...Array.from({length: count}, (_, i) => {
        const button = document.createElement('button'); button.type = 'button';
        button.setAttribute('aria-label', `Show photo ${i + 1}`);
        button.innerHTML = '<span class="yv-photo-pip" aria-hidden="true"></span>';
        button.addEventListener('click', () => choose(i)); return button;
      }));
    }
    [...container.children].forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    container.hidden = count < 2;
  }
  function fadeCaption(caption, text) {
    if (!caption || caption.textContent === text) return;
    caption.textContent = text;
    caption.getAnimations().forEach(animation => animation.cancel());
    if (!reduced.matches) caption.animate([{opacity: .2}, {opacity: 1}], {duration: 420, easing: 'ease-out'});
  }
  window.YVSwipeRail = SwipeRail;
  window.YVDragScroll = dragScroll;
  window.YVPhotoDots = photoDots;
  window.YVFadeCaption = fadeCaption;

  if (!customElements.get('yv-image-slider')) customElements.define('yv-image-slider', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort(); this.slider?.destroy(); this.events = new AbortController();
      const signal = this.events.signal, rail = this.querySelector('[data-yv-slider-track]');
      if (!rail) return;
      const controls = this.querySelector('[data-yv-slider-controls]');
      const previous = this.querySelector('[data-yv-slider-previous]'), next = this.querySelector('[data-yv-slider-next]');
      const dots = this.querySelector('[data-yv-slider-dots]');
      this.slider = new SwipeRail(rail, {onChange: (index, item, count) => {
        if (controls) controls.hidden = count < 2;
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === count - 1;
        const counter = this.querySelector('[data-yv-slider-count]');
        if (counter) counter.textContent = `Photo ${index + 1} of ${count}`;
        photoDots(dots, count, index, i => this.slider.go(i));
        if (item.dataset.label) fadeCaption(this.querySelector('[data-yv-slider-caption]'), item.dataset.label);
      }});
      rail.addEventListener('yv:fade-progress', event => {
        dots?.querySelectorAll('.yv-photo-pip').forEach((pip, i) => pip.style.setProperty('--photo-weight', event.detail.weights[i] || 0));
      }, {signal});
      previous?.addEventListener('click', () => this.slider.move(-1), {signal});
      next?.addEventListener('click', () => this.slider.move(1), {signal});
    }
    disconnectedCallback() { this.events?.abort(); this.slider?.destroy(); }
  });
})();
